import BottomArea from "./BottomArea.js";
import Room from "../Room.js";
import global from "../global.js";

const log = global.log.getLogger("f_mtac.ui.DesktopSelector");
log.setLevel(log.levels.WARN);

const DESKTOP_STATE_EMPTY = 0,
    DESKTOP_STATE_HAS_MAP = 1,
    DESKTOP_STATE_HAS_GEO = 2;

const Desktop = function (desktop_index) {
    var state = DESKTOP_STATE_EMPTY;

    Object.defineProperty(this, "index", {
        set: () => {},
        get: () => desktop_index
    });

    Object.defineProperty(this, "state", {
        set: (new_state) => {
            let $button = this.tool.$button;
            switch (new_state) {
                case DESKTOP_STATE_EMPTY:
                    $button.removeClass("desktop");
                    $button.removeClass("georef-desktop");
                    $button.addClass("empty-desktop");
                    $button.removeAttr("disabled");
                    if (!(global.configuration.profiles[global.configuration.user.level].rights & global.configuration.rights.PROFILE_RIGHTS_SPECIAL) && global.storage.shared()) {
                        $button.attr("disabled", true);
                    }
                    $button.find("i:not(.notification-marker)").removeClass("f_mtac-icon-map-solid").addClass("f_mtac-icon-map-regular");
                    break;
                case DESKTOP_STATE_HAS_MAP:
                    $button.removeAttr("disabled");
                    $button.removeClass("empty-desktop");
                    $button.removeClass("georef-desktop");
                    $button.addClass("desktop");
                    $button.find("i:not(.notification-marker)").removeClass("f_mtac-icon-map-regular").addClass("f_mtac-icon-map-solid");
                    break;
                case DESKTOP_STATE_HAS_GEO:
                    $button.removeAttr("disabled");
                    $button.removeClass("empty-desktop");
                    $button.addClass("georef-desktop");
                    $button.addClass("desktop");
                    $button.find("i:not(.notification-marker)").removeClass("f_mtac-icon-map-regular").addClass("f_mtac-icon-map-solid");
                    break;
            }
            state = new_state;
        },
        get: () => state
    });
    this.id = Room.desktop_index_to_desktop_id(desktop_index);
    this.label = String(desktop_index + 1);
    this.tool = null;
};

const DesktopSelector = function () {
    const MAX_DISPLAYED_DESKTOP = 9;
    var desktops = Array.from(Array(MAX_DISPLAYED_DESKTOP), (_, desktop_index) => new Desktop(desktop_index));
    var selected_desktop = desktops[0];

    Object.defineProperty(this, "index", {
        set: (index) => {
            selected_desktop.tool?.$button.removeClass("btn-selected");
            selected_desktop = desktops[index];
            selected_desktop.tool?.$button.addClass("btn-selected");
        },
        get: () => selected_desktop.index
    });

    Object.defineProperty(this, "desktop", {
        set: () => {},
        get: () => selected_desktop
    });

    this.refresh_desktop = async (desktop_index) => {
        if (global.storage.selected_space === null) {
            return;
        }
        let context = global.storage.selected_space.context[desktop_index];
        let desktop = desktops[desktop_index];
        if (context === null) {
            log.debug("Refresh desktop " + desktop_index + " > EMPTY");
            desktop.state = DESKTOP_STATE_EMPTY;
            return;
        }
        var map_media_id = await context.map_media_id;
        log.debug("DesktopSelector.refresh_desktop", {map_media_id});
        if (map_media_id === null) {
            log.debug("Refresh desktop " + desktop_index + " > EMPTY");
            desktop.state = DESKTOP_STATE_EMPTY;
            return;
        }
        if (context.projection.project !== null) {
            log.debug("Refresh desktop " + desktop_index + " > HAS_GEO");
            desktop.state = DESKTOP_STATE_HAS_GEO;
        } else {
            log.debug("Refresh desktop " + desktop_index + " > HAS_MAP");
            desktop.state = DESKTOP_STATE_HAS_MAP;
        }
    };
    this.refresh_all = async () => {
        if (global.storage.selected_space === null) {
            return;
        }
        for (let desktop_index = 0; desktop_index < global.storage.selected_space.context_number; desktop_index++) {
            this.refresh_desktop(desktop_index);
        }
    };

    global.event_bus.subscribe("storage.context.created", ({desktop_index}) => {
        log.debug("DesktopSelector.on.storage.context.created:", desktop_index);
        if (global.storage.selected_space === null) {
            return;
        }
        this.refresh_desktop(desktop_index);
        if (selected_desktop.index === desktop_index) {
            // Open selected_desktop because it was created by someone else (otherwise, selected_desktop would be different than desktop_index).
            // TODO: find which function executes space.create_context() and try to replace by open_context(). But maybe executing the storage.open() is necessary anyway...
            global.storage.open({desktop_index: selected_desktop.index});
        }
    });
    global.event_bus.subscribe("storage.context.updated", ({desktop_index}) => {
        // Another user have initialized/cleared/georeferenced/degeoreferenced a desktop
        log.debug("DesktopSelector.on.storage.context.updated:", desktop_index);
        // AGPOC-6408/6666: SITAC status not updated after a modification by another user
        this.refresh_desktop(desktop_index);
    });
    global.event_bus.subscribe("storage.context.deleted", ({desktop_index}) => {
        log.debug("DesktopSelector.on.storage.context.deleted:", desktop_index);
        this.refresh_desktop(desktop_index);
    });
    global.event_bus.subscribe("storage.context.changed", ({new_context}) => {
        // Switched desktop
        log.debug("DesktopSelector.on.storage.context.changed:", new_context);
        this.refresh_all();
        if (new_context === null) {
            return;
        }
        if (this.index === new_context.index) {
            return;
        }
        this.index = new_context.index;
    });
    global.event_bus.subscribe("scene.map.initialized", () => {
        log.debug("DesktopSelector.on.scene.map.initialized");
        this.refresh_desktop(selected_desktop.index);
    });
    global.event_bus.subscribe("scene.map.state", () => {
        // Manage desktops georeferencing status modification...
        // + space switching > This is why we need to update all the desktops. TODO: Find something better.
        log.debug("DesktopSelector.on.scene.map.state");
        this.refresh_all();
    });
    global.event_bus.subscribe("storage.shared", () => {
        // User rights are different on shared space
        log.debug("DesktopSelector.on.storage.shared");
        this.refresh_all();
    });
    global.event_bus.subscribe("storage.unshared", () => {
        // User rights are different on local space
        log.debug("DesktopSelector.on.storage.unshared");
        this.refresh_all();
    });
    global.event_bus.subscribe("user.level.changed", () => {
        // User rights modified. The user may not be able to open empty desktops.
        log.debug("DesktopSelector.on.user.level.changed");
        this.refresh_all();
    });

    global.event_bus.subscribe("mode_switcher.switcher_left", ({mode}) => {
        // Show selected desktop when switching logs area from fullscreen to panel mode
        if (mode === "tactics") {
            const current_desktop = this.index;
            this.index = current_desktop;
        }
    });

    global.app.bootstrap.register_module({
        name: "ui_desktop_selector",
        deps: ["dom_loaded", "scene", "tools", "storage"],
        init: async () => {
            desktops.forEach((desktop) => {
                desktop.tool = global.tools.register({
                    position: "#desktop_selector",
                    name: `desktop-item-${desktop.label}`,
                    classes: "desktop-selector",
                    icon: "map-regular",
                    value: desktop.label,
                    text: "&nbsp" + desktop.label,
                    handler: () => {
                        log.debug(`DesktopSelector.handler.desktop-item-${desktop.index}`);
                        this.index = desktop.index;
                        global.storage.open({desktop_index: desktop.index});
                        BottomArea.switcher_left.mode = "tactics";
                    }
                });
            });
            this.index = 0;
        }
    });
};

export default new DesktopSelector();
